<template>
    <div class="fcs">
        <button v-if="phase == 'idle'" type="button" class="ai-btn ai-btn-ghost fcs-start" @click="draft()">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>
            Break into steps
        </button>
        <div v-else-if="phase == 'loading'" class="fcs-loading" role="status">The Forge is writing the steps…</div>
        <div v-else class="fcs-box">
            <div class="fcs-head">Steps from the Forge <span>Edit, untick or add, then add them as a checklist.</span></div>
            <div v-for="(st, i) in steps" :key="i" class="fcs-step" :class="{ 'fcs-off': !st.keep }">
                <input type="checkbox" v-model="st.keep" :aria-label="'Keep step ' + (i + 1)" />
                <textarea v-model="st.name" rows="1" maxlength="500" aria-label="Step"></textarea>
                <input type="date" v-model="st.date" aria-label="Date" />
            </div>
            <a class="fcs-add" href="#" @click.prevent="steps.push({ name: '', date: '', keep: true })">+ Add a step</a>
            <div v-if="error" class="fcs-error" role="alert">{{ error }}</div>
            <div class="fcs-actions">
                <button type="button" class="fcs-link" @click="reset()">Cancel</button>
                <button type="button" class="ai-btn" :disabled="!kept.length" @click="add()">Add {{ kept.length }} step{{ kept.length == 1 ? '' : 's' }} as a checklist</button>
            </div>
        </div>
        <div v-if="phase == 'idle' && error" class="fcs-error" role="alert">{{ error }}</div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

// "Break into steps" on a board card (AddEditCompanyBoardItem.vue): the Forge (team-draft kind cardsteps) writes a
// checklist from the card's name and description; the person edits it and the card adds it as a checklist.
export default defineComponent({
    name: 'ForgeCardSteps',
    // card: { name, description (HTML), dueDate, existingSteps: [names] }
    props: { card: { type: Object, required: true } },
    emits: [ 'add' ],
    data() { return { phase: 'idle', steps: [] as any[], error: '' }; },
    computed: {
        kept(): any[] { return this.steps.filter((s: any) => s.keep && String(s.name || '').trim()); }
    },
    methods: {
        today(): string { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; },
        async draft() {
            this.phase = 'loading';
            this.error = '';
            const c: any = this.card;
            const description = String(c.description || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 4000);
            try {
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/ai/team-draft`, {
                    kind: 'cardsteps', description: 'Break this card into steps.',
                    context: { today: this.today(), card: { name: c.name, description, dueDate: c.dueDate || null, existingSteps: c.existingSteps || [] } }
                });
                const ok = (s: any) => typeof s == 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && s >= this.today();
                this.steps = (resp.data.steps || []).filter((s: any) => String(s.name || '').trim()).slice(0, 15)
                    .map((s: any) => ({ name: String(s.name).trim(), date: ok(s.date) ? s.date : '', keep: true }));
                if(!this.steps.length) throw new Error('none');
                this.phase = 'review';
            } catch(e: any) {
                const d = e?.response?.data;
                this.error = typeof d == 'string' && d && d.length < 300 ? d : 'The Forge couldn\'t write steps for this card. Please try again.';
                this.phase = 'idle';
            }
        },
        add() {
            this.$emit('add', this.kept.map((s: any) => ({ name: String(s.name).trim(), checked: false, ...(s.date ? { date: s.date } : {}) })));
            this.reset();
        },
        reset() { this.phase = 'idle'; this.steps = []; this.error = ''; }
    }
});
</script>

<style scoped>
.fcs { margin: 14px 0 6px; }
.fcs-start { display: inline-flex; align-items: center; gap: 6px; }
.fcs-loading { color: #8a7f75; font-style: italic; font-size: 14px; }
.fcs-box { background: #fff8f1; border: 1px solid #f3dcc7; border-radius: 12px; padding: 12px 14px; display: grid; gap: 8px; }
.fcs-head { font-weight: 700; font-size: 14px; color: #3a2f27; }
.fcs-head span { font-weight: 400; color: #8a7f75; margin-left: 6px; }
.fcs-step { display: grid; grid-template-columns: 18px minmax(0, 1fr) 140px; gap: 8px; align-items: center; }
.fcs-step input[type=checkbox] { width: 16px; height: 16px; margin: 0; }
.fcs-step textarea { height: auto !important; min-height: 32px; field-sizing: content; resize: none; margin: 0; padding: 5px 8px; font-size: 14px; line-height: 1.35; border: 1px solid #ecdccd; border-radius: 8px; background: #fff; }
.fcs-step input[type=date] { margin: 0; padding: 4px 6px; font-size: 13px; }
.fcs-off textarea { opacity: .5; text-decoration: line-through; }
.fcs-add { font-size: 13.5px; color: #c2410c; font-weight: 700; }
.fcs-error { color: #a52c26; font-size: 13.5px; }
.fcs-actions { display: flex; justify-content: flex-end; align-items: center; gap: 12px; }
.fcs-link { background: none; border: 0; color: #8a7f75; text-decoration: underline; cursor: pointer; font: inherit; font-size: 14px; }
@media (max-width: 640px) { .fcs-step { grid-template-columns: 18px minmax(0, 1fr); } .fcs-step input[type=date] { grid-column: 2; } }
</style>
